import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { KeyRound, Lock, Unlock, CheckCircle, XCircle, AlertTriangle, ChevronRight } from 'lucide-react';
import { ITEMS, LOCKPICK } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import CONFIG from '@/config/siteConfig';
import { LOCKPICK as LOCKPICK_CONTENT } from '@/config/missionContent';

// Build pin metadata from constants
const PINS = LOCKPICK.pins.map((p, i) => ({
  ...p,
  id: i,
  label: `PIN ${i + 1}`,
}));
const MAX_PICKS = LOCKPICK.maxPicks;
const DIFFICULTY_LABELS = ['BASIC', 'MODERATE', 'ADVANCED', 'EXPERT', 'MASTER'];

/* ── Enhanced Background ── */
function LockBackground({ active, tension }) {
  const particles = useMemo(() =>
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      left: `${8 + (i * 8.3) % 84}%`,
      top: `${5 + (i * 12.7) % 90}%`,
      size: 1.5 + (i % 3),
      duration: 7 + (i % 4) * 2,
      delay: i * 0.6,
      color: i % 3 === 0 ? 'bg-yellow-500/15' : i % 3 === 1 ? 'bg-amber-500/10' : 'bg-yellow-400/8',
    })), []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-950" />

      {/* Metallic texture */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `repeating-linear-gradient(
            90deg, transparent, transparent 1px, rgba(255,255,255,0.03) 1px, rgba(255,255,255,0.03) 2px
          )`,
        }}
      />

      {/* Floating particles */}
      {particles.map(p => (
        <motion.div
          key={p.id}
          animate={{ y: [0, -25, 0], x: [0, 8, -8, 0], opacity: [0.15, 0.4, 0.15] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: 'easeInOut' }}
          className={`absolute rounded-full ${p.color}`}
          style={{ left: p.left, top: p.top, width: p.size, height: p.size }}
        />
      ))}

      {/* Amber vignette */}
      <div
        className="absolute inset-0 animate-pulse"
        style={{
          animationDuration: '5s',
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(120,80,0,0.12) 100%)',
        }}
      />

      {/* CRT scanlines */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(234,179,8,0.12) 2px, rgba(234,179,8,0.12) 3px)',
          backgroundSize: '100% 3px',
        }}
      />

      {/* Tension glow */}
      {active && (
        <motion.div
          className="absolute inset-0"
          animate={{
            background: tension
              ? 'radial-gradient(circle at 50% 50%, rgba(234,179,8,0.06) 0%, transparent 60%)'
              : 'radial-gradient(circle at 50% 50%, rgba(234,179,8,0.02) 0%, transparent 60%)',
          }}
          transition={{ duration: 0.3 }}
        />
      )}
    </div>
  );
}

/* ── HUD Corners ── */
function HUDCorners({ color = 'border-yellow-700/30' }) {
  return (
    <>
      <div className={`absolute top-0 left-0 w-6 h-6 border-t border-l ${color}`} />
      <div className={`absolute top-0 right-0 w-6 h-6 border-t border-r ${color}`} />
      <div className={`absolute bottom-0 left-0 w-6 h-6 border-b border-l ${color}`} />
      <div className={`absolute bottom-0 right-0 w-6 h-6 border-b border-r ${color}`} />
    </>
  );
}

/* ── Tutorial Screen ── */
function LockpickTutorial({ onStart }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-md mx-auto relative z-10"
    >
      <div className="bg-black/60 backdrop-blur-sm border border-yellow-900/30 rounded-lg p-6 relative">
        <HUDCorners />

        {/* Header */}
        <div className="text-center mb-5">
          <div className="flex items-center justify-center gap-2 mb-2">
            <KeyRound size={14} className="text-yellow-500" />
            <span className="font-terminal text-xs tracking-[4px] text-yellow-500 uppercase">Lockpick Protocol</span>
            <KeyRound size={14} className="text-yellow-500" />
          </div>
          <div className="w-16 h-px bg-gradient-to-r from-transparent via-yellow-700/50 to-transparent mx-auto" />
        </div>

        {/* How it works */}
        <div className="mb-5">
          <h3 className="font-terminal text-xs tracking-[3px] text-yellow-400 uppercase mb-3">How It Works</h3>
          <div className="space-y-2">
            {[
              'Hold to apply tension — the pick moves across the bar',
              'Release when the pick is inside the green sweet spot zone',
              'Each pin gets faster with a narrower sweet spot',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-2">
                <ChevronRight size={10} className="text-yellow-600 mt-0.5 shrink-0" />
                <span className="text-xs text-gray-400 font-rajdhani">{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Visual diagram */}
        <div className="mb-5 bg-black/40 border border-yellow-900/20 rounded p-3">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={10} className="text-yellow-600" />
            <span className="text-xs font-terminal tracking-[3px] text-yellow-600 uppercase">Pick Bar</span>
          </div>
          <div className="relative h-8 bg-gray-900 rounded border border-gray-700/40 overflow-hidden">
            {/* Sweet spot zone */}
            <div
              className="absolute top-0 bottom-0"
              style={{
                left: '38%',
                width: '24%',
                background: 'linear-gradient(180deg, rgba(34,197,94,0.3) 0%, rgba(34,197,94,0.1) 50%, rgba(34,197,94,0.3) 100%)',
                borderLeft: '1px solid rgba(34,197,94,0.5)',
                borderRight: '1px solid rgba(34,197,94,0.5)',
              }}
            />
            <div className="absolute top-0 bottom-0 w-px bg-green-400/40" style={{ left: '50%' }} />
            {/* Pick indicator */}
            <motion.div
              animate={{ left: ['20%', '65%', '20%'] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute top-1 bottom-1 w-1 rounded-full bg-yellow-400"
              style={{ boxShadow: '0 0 6px rgba(234,179,8,0.6)' }}
            />
            {/* Label */}
            <span className="absolute top-1/2 -translate-y-1/2 text-[6px] font-terminal text-green-400/60 tracking-wider" style={{ left: '42%' }}>SWEET SPOT</span>
          </div>
        </div>

        {/* Warning */}
        <div className="flex items-center gap-2 bg-red-950/20 border border-red-800/30 rounded px-3 py-2 mb-5">
          <AlertTriangle size={12} className="text-red-500 shrink-0" />
          <span className="text-xs text-red-400 font-terminal tracking-wider">MISS TOO MANY TIMES AND YOUR PICK BREAKS</span>
        </div>

        {/* Start button */}
        <motion.button
          onClick={onStart}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full py-3 bg-gradient-to-r from-yellow-900/40 via-amber-800/50 to-yellow-900/40 border border-yellow-700/40 rounded font-terminal text-sm tracking-[4px] text-yellow-300 uppercase relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-500/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
          <span className="relative flex items-center justify-center gap-2">
            <KeyRound size={14} />
            Begin Picking
            <KeyRound size={14} />
          </span>
          <motion.div
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 border border-yellow-500/20 rounded"
          />
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ── HUD ── */
function LockpickHUD({ currentPin, totalPins, active }) {
  const diffLabel = active ? (DIFFICULTY_LABELS[currentPin] || 'UNKNOWN') : 'COMPLETE';
  const diffColor = currentPin >= 3 ? 'text-red-400' : currentPin >= 2 ? 'text-yellow-400' : 'text-green-400';

  return (
    <div className="max-w-md mx-auto mb-4 relative z-10">
      <div className="bg-black/50 border border-yellow-900/30 rounded px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
            <span className="text-xs font-terminal text-yellow-700/60 tracking-[3px]">CHECKPOINT 08 // LOCKPICK</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-terminal text-gray-600">PIN:</span>
              <span className="text-sm font-orbitron text-yellow-400">{Math.min(currentPin + 1, totalPins)}/{totalPins}</span>
            </div>
            <div className="h-4 w-px bg-yellow-900/30" />
            <div className="flex items-center gap-1.5">
              <KeyRound size={9} className={diffColor} />
              <span className={`text-xs font-terminal tracking-wider ${diffColor}`}>{diffLabel}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Picks Remaining ── */
function PicksRemaining({ count, max }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: max }).map((_, i) => (
        <motion.div
          key={i}
          initial={false}
          animate={i < count ? { opacity: 1, scale: 1 } : { opacity: 0.2, scale: 0.8 }}
          className="relative"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M7 2L17 2L17 8L14 11L14 20L10 22L10 11L7 8Z"
              fill={i < count ? '#eab308' : '#374151'}
              stroke={i < count ? '#ca8a04' : '#1f2937'}
              strokeWidth="1"
            />
          </svg>
          {i >= count && (
            <div className="absolute inset-0 flex items-center justify-center">
              <XCircle size={10} className="text-red-500/60" />
            </div>
          )}
        </motion.div>
      ))}
      <span className="text-xs font-terminal text-gray-500 ml-1">PICKS</span>
    </div>
  );
}

/* ── Pin Indicators ── */
function PinIndicators({ lockedCount, currentPin }) {
  return (
    <div className="flex items-center gap-3 justify-center">
      {PINS.map((pin, i) => {
        const isLocked = i < lockedCount;
        const isCurrent = i === currentPin;
        return (
          <motion.div
            key={pin.id}
            className="flex flex-col items-center gap-1"
            animate={isCurrent ? { y: [0, -2, 0] } : {}}
            transition={isCurrent ? { duration: 1.5, repeat: Infinity, ease: 'easeInOut' } : {}}
          >
            <div
              className={`w-8 h-10 rounded-sm border-2 flex items-center justify-center transition-all duration-300
                ${isLocked
                  ? 'border-green-500/60 bg-green-950/30'
                  : isCurrent
                  ? 'border-yellow-500/60 bg-yellow-950/20'
                  : 'border-gray-700/40 bg-gray-900/20'
                }`}
              style={{
                boxShadow: isLocked
                  ? '0 0 8px rgba(34,197,94,0.2)'
                  : isCurrent
                  ? '0 0 8px rgba(234,179,8,0.15)'
                  : 'none',
              }}
            >
              {isLocked ? (
                <Unlock size={14} className="text-green-400" />
              ) : (
                <Lock size={14} className={isCurrent ? 'text-yellow-400' : 'text-gray-600'} />
              )}
            </div>
            <span className={`text-xs font-terminal tracking-wider
              ${isLocked ? 'text-green-500' : isCurrent ? 'text-yellow-500' : 'text-gray-600'}`}>
              {pin.label}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ── Main Component ── */
export default function Lockpick({ onComplete, onItemCollected }) {
  const [resetKey, setResetKey] = useState(0);

  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [active, setActive] = useState(false);
  const [currentPin, setCurrentPin] = useState(0);
  const [position, setPosition] = useState(0);
  const [lockedPins, setLockedPins] = useState(0);
  const [picksLeft, setPicksLeft] = useState(MAX_PICKS);
  const [tension, setTension] = useState(false);
  const [result, setResult] = useState(null);
  const [solved, setSolved] = useState(false);
  const [showFinalLeon, setShowFinalLeon] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [successPhase, setSuccessPhase] = useState(0);
  const [picksUsedTotal, setPicksUsedTotal] = useState(0);
  const [showProceed, setShowProceed] = useState(false);

  const animRef = useRef(null);
  const lastTimeRef = useRef(null);
  const dirRef = useRef(1);
  const posRef = useRef(0);

  // Mount-reset
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setActive(false);
    setCurrentPin(0);
    setPosition(0);
    setLockedPins(0);
    setPicksLeft(MAX_PICKS);
    setTension(false);
    setResult(null);
    setSolved(false);
    setShowFinalLeon(false);
    setShaking(false);
    setSuccessPhase(0);
    setPicksUsedTotal(0);
    setShowProceed(false);
    posRef.current = 0;
    dirRef.current = 1;
    lastTimeRef.current = null;
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
  }, [resetKey]);

  // Visibility-based reset
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        lastTimeRef.current = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    setActive(true);
    posRef.current = 0;
    dirRef.current = 1;
    lastTimeRef.current = null;
  }, []);

  // Frame-rate independent animation
  useEffect(() => {
    if (!active || solved || result) {
      if (animRef.current) {
        cancelAnimationFrame(animRef.current);
        animRef.current = null;
      }
      return;
    }
    const pin = PINS[currentPin];
    if (!pin) return;
    lastTimeRef.current = null;

    const tick = (timestamp) => {
      if (lastTimeRef.current === null) {
        lastTimeRef.current = timestamp;
        animRef.current = requestAnimationFrame(tick);
        return;
      }

      const delta = (timestamp - lastTimeRef.current) / 1000;
      lastTimeRef.current = timestamp;
      const clampedDelta = Math.min(delta, 0.1);

      let pos = posRef.current;
      let dir = dirRef.current;

      pos += pin.speed * dir * clampedDelta;

      if (pos >= 100) { pos = 100; dir = -1; }
      if (pos <= 0) { pos = 0; dir = 1; }

      dirRef.current = dir;
      posRef.current = pos;
      setPosition(pos);

      animRef.current = requestAnimationFrame(tick);
    };

    animRef.current = requestAnimationFrame(tick);
    return () => {
      if (animRef.current) {
        cancelAnimationFrame(animRef.current);
        animRef.current = null;
      }
    };
  }, [active, solved, currentPin, result]);

  // Handle tension (hold) mechanic
  const handlePointerDown = useCallback(() => {
    if (!active || solved || result) return;
    setTension(true);
  }, [active, solved, result]);

  const handlePointerUp = useCallback(() => {
    if (!active || solved || result || !tension) return;
    setTension(false);

    const pin = PINS[currentPin];
    const diff = Math.abs(posRef.current - pin.sweetSpot);

    if (diff <= pin.tolerance) {
      // Success
      setResult('success');

      setTimeout(() => {
        const newCount = lockedPins + 1;
        setLockedPins(newCount);
        setResult(null);

        if (newCount === PINS.length) {
          setSolved(true);
          setActive(false);
          // Multi-phase success
          setSuccessPhase(1);
          setTimeout(() => setSuccessPhase(2), 500);
          setTimeout(() => setSuccessPhase(3), 1200);
          setTimeout(() => setSuccessPhase(4), 1800);
          setTimeout(() => setShowFinalLeon(true), 2200);
        } else {
          setCurrentPin(newCount);
          setPicksLeft(MAX_PICKS);
          posRef.current = 0;
          dirRef.current = 1;
          lastTimeRef.current = null;
        }
      }, 600);
    } else {
      // Miss
      const remaining = picksLeft - 1;
      setPicksLeft(remaining);
      setPicksUsedTotal(prev => prev + 1);
      setShaking(true);
      setTimeout(() => setShaking(false), 400);

      if (remaining <= 0) {
        setResult('break');

        setTimeout(() => {
          setResult(null);
          // Full restart — back to pin 1
          setCurrentPin(0);
          setLockedPins(0);
          setPicksLeft(MAX_PICKS);
          posRef.current = 0;
          dirRef.current = 1;
          lastTimeRef.current = null;
        }, 1000);
      } else {
        setResult('fail');
        setTimeout(() => setResult(null), 400);
      }
    }
  }, [active, solved, result, tension, currentPin, lockedPins, picksLeft]);

  const handlePointerLeave = useCallback(() => {
    if (tension) setTension(false);
  }, [tension]);

  const handleFinalDismiss = useCallback(() => {
    setShowFinalLeon(false);
    onItemCollected?.(ITEMS.MASTER_KEY);
    setShowProceed(true);
  }, [onItemCollected]);

  const pin = PINS[currentPin];
  const isInZone = pin ? Math.abs(position - pin.sweetSpot) <= pin.tolerance : false;

  const zoneLeft = pin ? Math.max(0, pin.sweetSpot - pin.tolerance) : 0;
  const zoneWidth = pin ? Math.min(100, pin.sweetSpot + pin.tolerance) - zoneLeft : 0;

  return (
    <div
      className="puzzle-container relative overflow-hidden"
      data-testid="puzzle-lockpick"
      onPointerLeave={handlePointerLeave}
    >
      <LockBackground active={active && !solved} tension={tension} />

      {/* Header */}
      <div className="text-center mb-5 relative z-10">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-8 h-px bg-gradient-to-r from-transparent to-yellow-700/40" />
          <KeyRound size={12} className="text-yellow-500" />
          <p className="text-xs font-terminal tracking-[6px] text-yellow-500 uppercase">Checkpoint 08</p>
          <KeyRound size={12} className="text-yellow-500" />
          <div className="w-8 h-px bg-gradient-to-l from-transparent to-yellow-700/40" />
        </div>
        <h2 className="font-orbitron text-2xl tracking-wider text-gray-200 uppercase">Lockpick</h2>
        <p className="text-sm text-yellow-700/60 font-terminal mt-1">Hold &amp; release in the sweet spot</p>
      </div>

      {/* Tutorial */}
      <AnimatePresence>
        {showTutorial && <LockpickTutorial onStart={handleTutorialStart} />}
      </AnimatePresence>

      {/* HUD */}
      {(active || solved) && (
        <LockpickHUD currentPin={currentPin} totalPins={PINS.length} active={active} />
      )}

      {/* Pin progress */}
      {(active || solved) && (
        <div className="relative z-10 mb-6">
          <PinIndicators lockedCount={lockedPins} currentPin={solved ? -1 : currentPin} />
        </div>
      )}

      {/* Main lockpick area */}
      {active && !solved && pin && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center gap-5 relative z-10"
        >
          <div className={`w-full max-w-md px-4 ${shaking ? 'animate-shake' : ''}`}>
            {/* Main pick bar */}
            <div
              className={`relative h-16 rounded-lg border-2 overflow-hidden cursor-pointer select-none transition-all duration-200
                ${result === 'success'
                  ? 'border-green-500/70'
                  : result === 'fail' || result === 'break'
                  ? 'border-red-500/70'
                  : tension
                  ? 'border-yellow-500/50'
                  : 'border-gray-700/50'
                }`}
              style={{
                background: 'linear-gradient(180deg, #0a0a0a 0%, #111 50%, #0a0a0a 100%)',
                boxShadow: result === 'success'
                  ? '0 0 20px rgba(34,197,94,0.2), inset 0 0 20px rgba(34,197,94,0.05)'
                  : result === 'fail' || result === 'break'
                  ? '0 0 20px rgba(239,68,68,0.2), inset 0 0 20px rgba(239,68,68,0.05)'
                  : tension
                  ? '0 0 15px rgba(234,179,8,0.1), inset 0 0 15px rgba(0,0,0,0.3)'
                  : 'inset 0 0 20px rgba(0,0,0,0.4)',
              }}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              data-testid="lockpick-area"
            >
              {/* Tick marks */}
              {Array.from({ length: 51 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute top-0 w-px bg-gray-800"
                  style={{
                    left: `${(i / 50) * 100}%`,
                    height: i % 10 === 0 ? '10px' : i % 5 === 0 ? '6px' : '3px',
                  }}
                />
              ))}

              {/* Sweet spot zone */}
              <motion.div
                className="absolute top-0 bottom-0"
                initial={false}
                animate={{ opacity: isInZone ? 0.35 : 0.15 }}
                transition={{ duration: 0.15 }}
                style={{
                  left: `${zoneLeft}%`,
                  width: `${zoneWidth}%`,
                  background: 'linear-gradient(180deg, rgba(34,197,94,0.4) 0%, rgba(34,197,94,0.1) 50%, rgba(34,197,94,0.4) 100%)',
                  borderLeft: '2px solid rgba(34,197,94,0.5)',
                  borderRight: '2px solid rgba(34,197,94,0.5)',
                }}
              />

              {/* Sweet spot center line */}
              <div
                className="absolute top-0 bottom-0 w-px bg-green-400/40"
                style={{ left: `${pin.sweetSpot}%` }}
              />

              {/* Needle */}
              <motion.div
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
              >
                <div
                  className="absolute top-1 bottom-1 w-1 rounded-full"
                  style={{
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: result === 'success'
                      ? 'linear-gradient(180deg, #22c55e, #4ade80, #22c55e)'
                      : result === 'fail' || result === 'break'
                      ? 'linear-gradient(180deg, #ef4444, #f87171, #ef4444)'
                      : tension
                      ? isInZone
                        ? 'linear-gradient(180deg, #22c55e, #86efac, #22c55e)'
                        : 'linear-gradient(180deg, #eab308, #fde047, #eab308)'
                      : isInZone
                      ? 'linear-gradient(180deg, #22c55e, #4ade80, #22c55e)'
                      : 'linear-gradient(180deg, #eab308, #fbbf24, #eab308)',
                    boxShadow: result === 'success'
                      ? '0 0 12px rgba(34,197,94,0.8)'
                      : result === 'fail' || result === 'break'
                      ? '0 0 12px rgba(239,68,68,0.8)'
                      : isInZone
                      ? '0 0 10px rgba(34,197,94,0.6)'
                      : '0 0 8px rgba(234,179,8,0.5)',
                  }}
                />
                <div
                  className="absolute top-0 bottom-0 w-6 -left-[10px] rounded-full opacity-20"
                  style={{
                    background: result === 'success'
                      ? 'rgba(34,197,94,0.5)'
                      : result === 'fail' || result === 'break'
                      ? 'rgba(239,68,68,0.5)'
                      : isInZone
                      ? 'rgba(34,197,94,0.3)'
                      : 'rgba(234,179,8,0.3)',
                    filter: 'blur(4px)',
                  }}
                />
              </motion.div>

              {/* Tension overlay */}
              {tension && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: isInZone
                      ? 'linear-gradient(180deg, rgba(34,197,94,0.05) 0%, transparent 50%, rgba(34,197,94,0.05) 100%)'
                      : 'linear-gradient(180deg, rgba(234,179,8,0.05) 0%, transparent 50%, rgba(234,179,8,0.05) 100%)',
                  }}
                />
              )}

              {/* Result overlay */}
              <AnimatePresence>
                {result === 'success' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 flex items-center justify-center bg-green-950/20"
                  >
                    <CheckCircle size={28} className="text-green-400" />
                  </motion.div>
                )}
                {result === 'break' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 flex items-center justify-center bg-red-950/20"
                  >
                    <div className="text-center">
                      <AlertTriangle size={22} className="text-red-400 mx-auto mb-1" />
                      <p className="text-xs font-terminal text-red-400 tracking-wider">LOCK JAMMED — RESTARTING FROM PIN 1</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Below bar info */}
            <div className="flex items-center justify-between mt-3 px-1">
              <PicksRemaining count={picksLeft} max={MAX_PICKS} />
              <div className="text-right">
                <p className="text-xs font-terminal text-gray-500 tracking-wider">
                  {tension
                    ? isInZone
                      ? <span className="text-green-400">RELEASE NOW</span>
                      : <span className="text-yellow-400">HOLDING...</span>
                    : 'HOLD TO TENSION'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Keyhole visualization */}
          <div className="relative w-24 h-24 mt-2">
            <motion.div
              animate={tension ? { scale: [1, 1.02, 1] } : {}}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="absolute inset-0 rounded-full border-2 border-gray-700/40"
              style={{
                background: 'radial-gradient(circle, #111 40%, #0a0a0a 100%)',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.6), 0 0 15px rgba(0,0,0,0.3)',
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div
                    className={`w-6 h-6 rounded-full border-2 transition-colors duration-200
                      ${result === 'success' ? 'border-green-500 bg-green-950/40'
                        : tension ? 'border-yellow-500 bg-yellow-950/20'
                        : 'border-gray-600 bg-black/40'
                      }`}
                  />
                  <div
                    className={`absolute top-5 left-1/2 -translate-x-1/2 w-2.5 h-5 rounded-b-sm transition-colors duration-200
                      ${result === 'success' ? 'bg-green-800/60' : tension ? 'bg-yellow-900/40' : 'bg-gray-800/40'}`}
                  />
                </div>
              </div>
              <motion.div
                animate={tension
                  ? { rotate: isInZone ? 12 : 6 }
                  : { rotate: 0 }
                }
                transition={{ duration: 0.15 }}
                className="absolute bottom-2 left-1/2 w-0.5 h-8 origin-top"
                style={{
                  transform: 'translateX(-50%)',
                  background: tension
                    ? 'linear-gradient(to bottom, #eab308, #ca8a04)'
                    : 'linear-gradient(to bottom, #4b5563, #374151)',
                }}
              />
            </motion.div>
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(55,65,81,0.3)" strokeWidth="2" />
              <motion.circle
                cx="50" cy="50" r="46"
                fill="none"
                stroke="#22c55e"
                strokeWidth="2"
                strokeDasharray={`${2 * Math.PI * 46}`}
                initial={{ strokeDashoffset: 2 * Math.PI * 46 }}
                animate={{ strokeDashoffset: 2 * Math.PI * 46 * (1 - lockedPins / PINS.length) }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                strokeLinecap="round"
                style={{ opacity: 0.6 }}
              />
            </svg>
          </div>

          {/* Status */}
          <p className="text-xs font-terminal text-gray-500 tracking-widest text-center">
            {pin.label} — {DIFFICULTY_LABELS[currentPin] || 'UNKNOWN'}
          </p>
        </motion.div>
      )}

      {/* Multi-phase success */}
      {solved && (
        <div className="relative z-10 mt-6">
          {/* Phase 1: Gold flash */}
          {successPhase >= 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0.6, 0] }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 bg-yellow-500/20 pointer-events-none rounded-lg"
            />
          )}

          <div className="text-center space-y-4">
            {/* Phase 2: KeyRound icon */}
            {successPhase >= 2 && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                className="relative w-20 h-20 mx-auto"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0 border-2 border-dashed border-green-500/20 rounded-full"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="w-16 h-16 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center"
                    style={{ boxShadow: '0 0 30px rgba(0,255,65,0.25)' }}
                  >
                    <KeyRound size={28} className="text-green-400" />
                  </div>
                </div>
                {/* Shockwave */}
                <motion.div
                  initial={{ scale: 0.5, opacity: 0.8 }}
                  animate={{ scale: 3, opacity: 0 }}
                  transition={{ duration: 1.5, ease: 'easeOut' }}
                  className="absolute inset-0 border border-yellow-500/40 rounded-full"
                />
              </motion.div>
            )}

            {/* Phase 3: Title */}
            {successPhase >= 3 && (
              <motion.p
                initial={{ letterSpacing: '2px', opacity: 0 }}
                animate={{ letterSpacing: '6px', opacity: 1 }}
                transition={{ duration: 0.8 }}
                className="font-orbitron text-green-400 text-sm"
              >
                LOCK PICKED
              </motion.p>
            )}

            {/* Phase 4: Stats */}
            {successPhase >= 4 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-2"
              >
                <div className="flex items-center justify-center gap-6">
                  <div className="text-center">
                    <div className="text-xs font-terminal text-gray-600 tracking-wider">PINS PICKED</div>
                    <div className="text-lg font-orbitron text-green-400">{PINS.length}</div>
                  </div>
                  <div className="h-8 w-px bg-gray-800" />
                  <div className="text-center">
                    <div className="text-xs font-terminal text-gray-600 tracking-wider">PICKS BROKEN</div>
                    <div className={`text-lg font-orbitron ${picksUsedTotal === 0 ? 'text-green-400' : 'text-yellow-400'}`}>{picksUsedTotal}</div>
                  </div>
                </div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-gray-400 font-rajdhani"
                  style={{ textShadow: '0 0 10px rgba(234,179,8,0.3)' }}
                >
                  Master Key acquired
                </motion.p>
              </motion.div>
            )}
          </div>
        </div>
      )}

      {showProceed && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="text-center mt-8 relative z-20"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onComplete?.()}
            className="inline-flex items-center gap-2 px-8 py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
          >
            <span className="relative z-10">PROCEED</span>
            <motion.div
              className="absolute inset-0"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
            />
          </motion.button>
        </motion.div>
      )}

      {/* Leon dialogues */}
      {showLeon && (
        <LeonDialogue
          message={LOCKPICK_CONTENT.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}

      {showFinalLeon && (
        <LeonDialogue
          message={LOCKPICK_CONTENT.leon.complete}
          onDismiss={handleFinalDismiss}
        />
      )}

      {/* Shake keyframes */}
      <style>{`
        @keyframes lockpick-shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-4px); }
          30% { transform: translateX(4px); }
          45% { transform: translateX(-3px); }
          60% { transform: translateX(3px); }
          75% { transform: translateX(-1px); }
          90% { transform: translateX(1px); }
        }
        .animate-shake { animation: lockpick-shake 0.4s ease-out; }
      `}</style>
    </div>
  );
}
